/**
 * PlaceSeasonCards – "Årstider" section for admin place editor.
 * Four small, calm cards showing season overlay status.
 */
import { useState } from 'react';
import { Leaf } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ALL_SEASONS, seasonLabelSv, type Season } from '@/lib/season';
import { useSeasonOverlays } from '@/hooks/useSeasonOverlays';
import type { SeasonOverlay } from '@/lib/api/placeSeasonOverlays';
import { PlaceSeasonOverlayEditor } from './PlaceSeasonOverlayEditor';

interface Props {
  placeId: string;
}

function isOverlayComplete(o: SeasonOverlay): boolean {
  return !!(o.summary_sv || o.hero_media_asset_id || (o.gallery_media_asset_ids?.length ?? 0) > 0 || o.did_you_know_sv || o.typical_duration);
}

export function PlaceSeasonCards({ placeId }: Props) {
  const { data: overlays = [] } = useSeasonOverlays(placeId);
  const [editingSeason, setEditingSeason] = useState<Season | null>(null);

  const overlayMap = new Map(overlays.map((o) => [o.season, o]));

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        {ALL_SEASONS.map((season) => {
          const overlay = overlayMap.get(season);
          const complete = overlay ? isOverlayComplete(overlay) : false;

          return (
            <button
              key={season}
              type="button"
              onClick={() => setEditingSeason(season)}
              className={cn(
                'flex flex-col items-start gap-1.5 rounded-lg border p-3 text-left transition-colors',
                'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'bg-card text-card-foreground',
              )}
            >
              <div className="flex items-center gap-1.5">
                <Leaf className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm font-medium">{seasonLabelSv(season)}</span>
              </div>
              <Badge
                variant={complete ? 'default' : 'secondary'}
                className={cn(
                  'text-[10px] px-1.5 py-0',
                  complete
                    ? 'bg-primary/10 text-primary border-primary/20'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {complete ? 'Klar' : 'Saknas'}
              </Badge>
            </button>
          );
        })}
      </div>

      {editingSeason && (
        <PlaceSeasonOverlayEditor
          placeId={placeId}
          season={editingSeason}
          overlay={overlayMap.get(editingSeason) ?? null}
          onClose={() => setEditingSeason(null)}
        />
      )}
    </>
  );
}
